import { EFilterScope } from '../../rules/constants/rule.enums';
import type { IPipelineEnvelope } from '../../rules/interfaces/envelope.interface';

export interface IFilterDocument extends IPipelineEnvelope {
  customer?: { id: string; externalId: string } | null;
}

export type TFilterLeafOperator =
  | '$eq'
  | '$neq'
  | '$gt'
  | '$gte'
  | '$lt'
  | '$lte'
  | '$in'
  | '$nin'
  | '$startsWith'
  | '$endsWith'
  | '$exist'
  | '$contains'
  | '$containsAny'
  | '$matches'
  | '$empty'
  | '$sizeGt'
  | '$between';

const LEAF_OPERATORS: readonly string[] = [
  '$eq',
  '$neq',
  '$gt',
  '$gte',
  '$lt',
  '$lte',
  '$in',
  '$nin',
  '$startsWith',
  '$endsWith',
  '$exist',
  '$contains',
  '$containsAny',
  '$matches',
  '$empty',
  '$sizeGt',
  '$between',
];

const LOGICAL_OPERATORS: readonly string[] = ['$and', '$or', '$not'];

const SYSTEM_PATH = /^\$[a-z][a-z0-9_]*$/;

interface IFieldRef {
  readonly $ref: string;
}

export type TFilterOperand = IFieldRef | unknown;

export type TFilterNode =
  | { kind: 'and'; nodes: TFilterNode[] }
  | { kind: 'or'; nodes: TFilterNode[] }
  | { kind: 'not'; node: TFilterNode }
  | { kind: 'leaf'; path: string; operator: TFilterLeafOperator; operand: TFilterOperand };

export class FilterDslError extends Error {}

const MAX_DEPTH = 12;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFieldRef(value: unknown): value is IFieldRef {
  return isPlainObject(value) && typeof value.$ref === 'string' && Object.keys(value).length === 1;
}

// Reject unknown `$` keys: a misspelled operator would silently become a pass-through.
export function parseFilterAst(expression: unknown, depth = 0): TFilterNode {
  if (depth > MAX_DEPTH) {
    throw new FilterDslError(`filter expression nested deeper than ${MAX_DEPTH} levels`);
  }
  if (!isPlainObject(expression)) {
    throw new FilterDslError('a filter expression must be an object');
  }

  const entries = Object.entries(expression);
  if (entries.length === 0) {
    return { kind: 'and', nodes: [] };
  }

  const nodes: TFilterNode[] = entries.map(([key, value]) => parseEntry(key, value, depth));

  return nodes.length === 1 ? nodes[0] : { kind: 'and', nodes };
}

function parseEntry(key: string, value: unknown, depth: number): TFilterNode {
  if (key === '$and' || key === '$or') {
    if (!Array.isArray(value)) {
      throw new FilterDslError(`${key} takes an array of expressions`);
    }
    return {
      kind: key === '$and' ? 'and' : 'or',
      nodes: value.map((branch) => parseFilterAst(branch, depth + 1)),
    };
  }

  if (key === '$not') {
    return { kind: 'not', node: parseFilterAst(value, depth + 1) };
  }

  if (key.startsWith('$')) {
    if (!SYSTEM_PATH.test(key)) {
      throw new FilterDslError(
        `unknown top-level operator "${key}"; expected a field path, a system path like ` +
          `$status, or one of ${LOGICAL_OPERATORS.join(', ')}`,
      );
    }
  }

  return parsePathEntry(key, value, depth);
}

function parsePathEntry(path: string, value: unknown, depth: number): TFilterNode {
  if (!isPlainObject(value) || isFieldRef(value)) {
    return { kind: 'leaf', path, operator: '$eq', operand: value };
  }

  const keys = Object.keys(value);
  const unknown = keys.filter((key) => !LEAF_OPERATORS.includes(key) && key !== '$not');
  if (unknown.length > 0) {
    throw new FilterDslError(
      `unknown operator(s) ${unknown.map((k) => `"${k}"`).join(', ')} on path "${path}"; ` +
        `expected one of ${LEAF_OPERATORS.join(', ')}, $not`,
    );
  }

  const nodes: TFilterNode[] = keys.map((operator) => {
    const operand = value[operator];

    if (operator === '$not') {
      return { kind: 'not', node: parsePathEntry(path, operand, depth + 1) };
    }
    assertOperand(operator as TFilterLeafOperator, operand, path);
    return { kind: 'leaf', path, operator: operator as TFilterLeafOperator, operand };
  });

  return nodes.length === 1 ? nodes[0] : { kind: 'and', nodes };
}

function assertOperand(operator: TFilterLeafOperator, operand: unknown, path: string): void {
  switch (operator) {
    case '$in':
    case '$nin':
    case '$containsAny':
      if (!Array.isArray(operand)) {
        throw new FilterDslError(`${operator} on "${path}" takes an array`);
      }
      if (operator !== '$containsAny') {
        for (const item of operand) assertWildcard(item, operator, path);
      }
      return;
    case '$exist':
    case '$empty':
      if (typeof operand !== 'boolean') {
        throw new FilterDslError(`${operator} on "${path}" takes true or false`);
      }
      return;
    case '$between':
      if (!Array.isArray(operand) || operand.length !== 2) {
        throw new FilterDslError(`$between on "${path}" takes [from, to]`);
      }
      return;
    case '$sizeGt':
      if (typeof operand !== 'number' || !Number.isInteger(operand) || operand < 0) {
        throw new FilterDslError(`$sizeGt on "${path}" takes a whole number`);
      }
      return;
    case '$matches':
      if (typeof operand !== 'string') {
        throw new FilterDslError(`$matches on "${path}" takes a pattern string`);
      }
      assertWildcard(operand, operator, path);
      return;
    default:
      return;
  }
}

export interface IFilterExplanation {
  matched: boolean;
  matchedConditions: string[];
  failedOn?: string;
}

/** Returns `FILTER_ABSENT` for a path that is not there — `null` and `undefined` are values. */
type TFilterPathResolver = (path: string) => unknown;

export interface ICompiledFilter {
  test(document: IFilterDocument, scope: EFilterScope): boolean;
  explain(document: IFilterDocument, scope: EFilterScope): IFilterExplanation;
}

export function compileFilter(condition: unknown): ICompiledFilter {
  return compileFilterAst(parseFilterAst(condition));
}

export function compileFilterAst(ast: TFilterNode): ICompiledFilter {
  return {
    test: (document, scope) =>
      evaluate(ast, (path) => resolvePath(document, scope, path), []).value,
    explain: (document, scope) => {
      const matchedConditions: string[] = [];
      const result = evaluate(ast, (path) => resolvePath(document, scope, path), matchedConditions);
      return result.value
        ? { matched: true, matchedConditions }
        : { matched: false, matchedConditions, failedOn: result.failedOn };
    },
  };
}

interface IEvaluation {
  value: boolean;
  failedOn?: string;
}

function evaluate(node: TFilterNode, resolve: TFilterPathResolver, matched: string[]): IEvaluation {
  switch (node.kind) {
    case 'and': {
      for (const child of node.nodes) {
        const result = evaluate(child, resolve, matched);
        if (!result.value) return result;
      }
      return { value: true };
    }
    case 'or': {
      const reasons: string[] = [];
      for (const child of node.nodes) {
        const scratch: string[] = [];
        const result = evaluate(child, resolve, scratch);
        if (result.value) {
          matched.push(...scratch);
          return { value: true };
        }
        if (result.failedOn) reasons.push(result.failedOn);
      }
      return { value: false, failedOn: `none of: ${reasons.join(' | ')}` };
    }
    case 'not': {
      const result = evaluate(node.node, resolve, []);
      return result.value
        ? { value: false, failedOn: `$not(${describe(node.node, resolve)})` }
        : { value: true };
    }
    case 'leaf': {
      const actual = resolve(node.path);
      const operand = resolveOperand(node.operand, resolve);
      const held = applyFilterOperator(node.operator, actual, operand);
      const rendering = renderLeaf(node, actual, operand);
      if (held) {
        matched.push(rendering);
        return { value: true };
      }
      return { value: false, failedOn: rendering };
    }
  }
}

function describe(node: TFilterNode, resolve: TFilterPathResolver): string {
  if (node.kind === 'leaf') {
    return renderLeaf(node, resolve(node.path), node.operand);
  }
  return node.kind;
}

export const FILTER_ABSENT: unique symbol = Symbol('absent');
const ABSENT = FILTER_ABSENT;

function resolvePath(document: IFilterDocument, scope: EFilterScope, path: string): unknown {
  if (path === 'path') return document.path;
  if (path === 'query') return document.query;

  if (path.startsWith('customer.')) {
    const field = path.slice('customer.'.length);
    const customer = document.customer;
    if (!customer) return ABSENT;
    if (field === 'id') return customer.id;
    if (field === 'externalId') return customer.externalId;
    return ABSENT;
  }

  if (path.startsWith('headers.')) {
    return ownValue(document.headers, path.slice('headers.'.length).toLowerCase());
  }
  if (path.startsWith('query.')) {
    return ownValue(document.parsed_query, path.slice('query.'.length));
  }
  if (path.startsWith('body.')) {
    return descend(document.body, path.slice('body.'.length));
  }

  switch (scope) {
    case EFilterScope.HEADERS:
      return ownValue(document.headers, path.toLowerCase());
    case EFilterScope.QUERY:
      return ownValue(document.parsed_query, path);
    case EFilterScope.PATH:
      return document.path;
    case EFilterScope.BODY:
    default:
      return descend(document.body, path);
  }
}

function ownValue(record: object | null | undefined, key: string): unknown {
  if (!record || !Object.hasOwn(record, key)) return ABSENT;
  return (record as Record<string, unknown>)[key] ?? ABSENT;
}

export function descendFilterPath(root: unknown, path: string): unknown {
  return path === '' ? (root === undefined ? ABSENT : root) : descend(root, path);
}

function descend(root: unknown, path: string): unknown {
  let cursor: unknown = root;
  for (const segment of path.split('.')) {
    if (cursor === null || cursor === undefined) return ABSENT;
    if (Array.isArray(cursor)) {
      const index = Number(segment);
      if (!Number.isInteger(index)) return ABSENT;
      cursor = cursor[index];
      continue;
    }
    if (typeof cursor !== 'object') return ABSENT;
    const record = cursor as Record<string, unknown>;
    // `in` walks the prototype: `body.constructor` would resolve to Object.
    if (!Object.hasOwn(record, segment)) return ABSENT;
    cursor = record[segment];
  }
  return cursor === undefined ? ABSENT : cursor;
}

function resolveOperand(operand: TFilterOperand, resolve: TFilterPathResolver): unknown {
  if (isFieldRef(operand)) return resolve(operand.$ref);
  if (Array.isArray(operand)) {
    return operand.map((item) => (isFieldRef(item) ? resolve(item.$ref) : item));
  }
  return operand;
}

export function applyFilterOperator(
  operator: TFilterLeafOperator,
  actual: unknown,
  operand: unknown,
): boolean {
  if (operator === '$exist') {
    return (actual !== ABSENT) === operand;
  }
  // Absent counts as empty: "is empty" is what a user means by a field nobody filled in.
  if (operator === '$empty') {
    return isEmptyValue(actual) === operand;
  }
  if (actual === ABSENT) {
    // An absent field satisfies `$neq`/`$nin`: not-there is not that value.
    return operator === '$neq' || operator === '$nin';
  }

  switch (operator) {
    case '$eq':
      return strictEqual(actual, operand);
    case '$neq':
      return !strictEqual(actual, operand);
    case '$gt':
      return compare(actual, operand) > 0;
    case '$gte':
      return compare(actual, operand) >= 0;
    case '$lt':
      return compare(actual, operand) < 0;
    case '$lte':
      return compare(actual, operand) <= 0;
    case '$in':
      return (operand as unknown[]).some((item) => matchesListItem(actual, item));
    case '$nin':
      return !(operand as unknown[]).some((item) => matchesListItem(actual, item));
    case '$startsWith':
      return typeof actual === 'string' && actual.startsWith(String(operand));
    case '$endsWith':
      return typeof actual === 'string' && actual.endsWith(String(operand));
    case '$contains':
      return containsValue(actual, operand);
    case '$containsAny':
      return (operand as unknown[]).some((item) => containsValue(actual, item));
    case '$matches':
      return typeof actual === 'string' && wildcardMatches(String(operand), actual);
    case '$sizeGt':
      return Array.isArray(actual) && actual.length > Number(operand);
    case '$between': {
      const [from, to] = operand as unknown[];
      return compare(actual, from) >= 0 && compare(actual, to) <= 0;
    }
    default:
      return false;
  }
}

function isEmptyValue(value: unknown): boolean {
  if (value === ABSENT || value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

function containsValue(actual: unknown, needle: unknown): boolean {
  if (typeof actual === 'string') {
    return typeof needle !== 'object' && actual.includes(String(needle));
  }
  if (Array.isArray(actual)) return actual.some((item) => strictEqual(item, needle));
  return false;
}

function strictEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date || b instanceof Date) return dateValue(a) === dateValue(b);
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

function matchesListItem(actual: unknown, item: unknown): boolean {
  if (typeof item === 'string' && item.includes('*') && typeof actual === 'string') {
    return wildcardMatches(item, actual);
  }
  return strictEqual(actual, item);
}

export const MAX_WILDCARD_STARS = 16;

const wildcardCache = new Map<string, string[]>();

/** Pieces between unescaped `*`; `\*` is a literal star. */
export function splitWildcard(pattern: string): string[] {
  const pieces: string[] = [];
  let current = '';
  for (let i = 0; i < pattern.length; i++) {
    const char = pattern[i];
    if (char === '\\' && pattern[i + 1] === '*') {
      current += '*';
      i++;
    } else if (char === '*') {
      pieces.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  pieces.push(current);
  return pieces;
}

// No RegExp: `.*` runs backtrack polynomially (`*a*a*a…b` took seconds). Leftmost
// indexOf per piece is enough for a `*`-only language and never looks back.
export function wildcardMatches(pattern: string, value: string): boolean {
  let pieces = wildcardCache.get(pattern);
  if (!pieces) {
    pieces = splitWildcard(pattern);
    if (wildcardCache.size < 1000) wildcardCache.set(pattern, pieces);
  }
  if (pieces.length === 1) return value === pieces[0];

  const first = pieces[0];
  const last = pieces[pieces.length - 1];
  if (value.length < first.length + last.length) return false;
  if (!value.startsWith(first) || !value.endsWith(last)) return false;

  const end = value.length - last.length;
  let cursor = first.length;
  for (let i = 1; i < pieces.length - 1; i++) {
    const piece = pieces[i];
    if (piece === '') continue;
    const at = value.indexOf(piece, cursor);
    if (at === -1 || at + piece.length > end) return false;
    cursor = at + piece.length;
  }
  return true;
}

function assertWildcard(pattern: unknown, operator: string, path: string): void {
  if (typeof pattern !== 'string') return;
  if (splitWildcard(pattern).length - 1 > MAX_WILDCARD_STARS) {
    throw new FilterDslError(
      `${operator} on "${path}": a pattern takes at most ${MAX_WILDCARD_STARS} "*"`,
    );
  }
}

function dateValue(value: unknown): number | null {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function compare(a: unknown, b: unknown): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);

  const [da, db] = [dateValue(a), dateValue(b)];
  if (da !== null && db !== null && (a instanceof Date || b instanceof Date)) return da - db;

  if (typeof a === 'string' && typeof b === 'string') {
    if (isIsoDate(a) && isIsoDate(b)) return Date.parse(a) - Date.parse(b);
    return a < b ? -1 : a > b ? 1 : 0;
  }
  return Number.NaN;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;

function isIsoDate(value: string): boolean {
  return ISO_DATE.test(value) && !Number.isNaN(Date.parse(value));
}

function renderLeaf(
  node: Extract<TFilterNode, { kind: 'leaf' }>,
  actual: unknown,
  operand: unknown,
): string {
  const seen = actual === ABSENT ? '<absent>' : short(actual);
  const want = short(operand);

  switch (node.operator) {
    case '$eq':
      return `${node.path} ${seen} = ${want}`;
    case '$neq':
      return `${node.path} ${seen} ≠ ${want}`;
    case '$gt':
      return `${node.path} ${seen} > ${want}`;
    case '$gte':
      return `${node.path} ${seen} ≥ ${want}`;
    case '$lt':
      return `${node.path} ${seen} < ${want}`;
    case '$lte':
      return `${node.path} ${seen} ≤ ${want}`;
    case '$in':
      return `${node.path} ${seen} $in ${want}`;
    case '$nin':
      return `${node.path} ${seen} $nin ${want}`;
    case '$startsWith':
      return `${node.path} ${seen} startsWith ${want}`;
    case '$endsWith':
      return `${node.path} ${seen} endsWith ${want}`;
    case '$exist':
      return `${node.path} ${operand === true ? 'exists' : 'does not exist'} (${seen})`;
    case '$contains':
      return `${node.path} ${seen} contains ${want}`;
    case '$containsAny':
      return `${node.path} ${seen} containsAny ${want}`;
    case '$matches':
      return `${node.path} ${seen} matches ${want}`;
    case '$empty':
      return `${node.path} ${operand === true ? 'is empty' : 'is not empty'} (${seen})`;
    case '$sizeGt':
      return `${node.path} ${seen} has more than ${want}`;
    case '$between':
      return `${node.path} ${seen} between ${want}`;
    default:
      return node.path;
  }
}

function short(value: unknown): string {
  if (value === ABSENT) return '<absent>';
  let text: string;
  try {
    text =
      typeof value === 'string' ? JSON.stringify(value) : (JSON.stringify(value) ?? String(value));
  } catch {
    text = String(value);
  }
  return text.length > 120 ? `${text.slice(0, 117)}…` : text;
}
